import { useState } from "react";
import { ShieldCheck, X, Eye, EyeOff, Wifi, WifiOff } from "lucide-react";
import { useAuth } from "../contexts/AuthContext.tsx";
import { useLanguage } from "../contexts/LanguageContext.tsx";
import { useOnlineStatus } from "../hooks/useOnlineStatus.ts";
import { supabase } from "../lib/supabaseClient.ts";

// Check if Supabase is configured
const isSupabaseConfigured = !!(
  import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY
);

export default function AuthModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const { signIn, signUp } = useAuth();
  const isOnline = useOnlineStatus();
  const [tab, setTab] = useState<"signin" | "signup">("signin");
  const [showPassword, setShowPassword] = useState(false);

  // Auth state
  const [signInLoading, setSignInLoading] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [signUpError, setSignUpError] = useState<string | null>(null);
  const [signUpSuccess, setSignUpSuccess] = useState<string | null>(null);

  if (!open) return null;

  // Social login handler
  const handleOAuthSignIn = async (provider: "google" | "github") => {
    if (!isSupabaseConfigured) {
      setSignInError(t("auth.authServiceNotConfigured"));
      return;
    }

    try {
      const redirectTo = window.location.hostname === 'localhost' 
        ? 'http://localhost:3000/' 
        : `${window.location.origin}/`;
      console.log('OAuth redirect URL:', redirectTo);
      await supabase.auth.signInWithOAuth({ 
        provider,
        options: {
          redirectTo
        }
      });
    } catch (err: any) {
      setSignInError(t("auth.oauthFailed"));
    }
  };

  // Sign In handler
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignInError(null);
    setSignInLoading(true);

    const form = e.target as HTMLFormElement;
    const email = (form.elements.namedItem("email") as HTMLInputElement).value;
    const password = (form.elements.namedItem("password") as HTMLInputElement)
      .value;

    try {
      const { user, error } = await signIn(email, password);

      if (error) {
        setSignInError(error.message);
      } else if (user) {
        console.log('Sign in successful, closing modal');
        // Small delay to allow state to propagate
        setTimeout(() => onClose(), 100);
      } else {
        console.log('Sign in result:', { user, error });
      }
    } catch (err: any) {
      console.error('Sign in error:', err);
      setSignInError(err.message || t("auth.signInFailed"));
    } finally {
      setSignInLoading(false);
    }
  };

  // Sign Up handler
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignUpError(null);
    setSignUpSuccess(null);
    setSignUpLoading(true);

    const form = e.target as HTMLFormElement;
    const fullName = (form.elements.namedItem("fullName") as HTMLInputElement)
      .value;
    const email = (form.elements.namedItem("email") as HTMLInputElement).value;
    const password = (form.elements.namedItem("password") as HTMLInputElement)
      .value;

    try {
      const { user, error } = await signUp(email, password, fullName);

      if (error) {
        setSignUpError(error.message);
      } else if (user) {
        if (isOnline) {
          setSignUpSuccess(
            t("auth.accountCreated")
          );
        } else {
          setSignUpSuccess(
            t("auth.accountCreatedOffline")
          );
          setTimeout(() => onClose(), 2000);
        }
      }
    } catch (err: any) {
      setSignUpError(err.message || t("auth.accountCreationFailed"));
    } finally {
      setSignUpLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative w-full max-w-md p-0 mx-auto bg-white shadow-xl rounded-xl">
        {/* Close button */}
        <button
          className="absolute text-gray-400 top-4 right-4 hover:text-gray-700"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>
        <div className="p-8 pb-6">
          <div className="flex items-center mb-2">
            <ShieldCheck className="w-6 h-6 mr-2 text-green-600" />
            <span className="text-lg font-bold">{t("auth.welcomeToAgroSense")}</span>
          </div>
          <div className="flex items-center gap-2 mb-2">
            {isOnline ? (
              <>
                <Wifi className="w-4 h-4 text-green-500" />
                <span className="text-sm text-green-600">Online</span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4 text-orange-500" />
                <span className="text-sm text-orange-600">Offline Mode</span>
              </>
            )}
          </div>
          <div className="mb-6 text-sm text-gray-500">
            {isOnline
              ? "Secure access to your personalized farming dashboard"
              : "Works offline - your data will sync when you're back online"}
          </div>

          {/* Social Auth - Only show when online */}
          {isOnline && (
            <div className="flex flex-col gap-3 my-4">
              <button
                type="button"
                className="flex items-center justify-center gap-2 px-4 py-2 font-medium transition border border-gray-200 rounded-md shadow-sm hover:bg-gray-50"
                onClick={() => handleOAuthSignIn("google")}
              >
                <img
                  src="https://www.svgrepo.com/show/475656/google-color.svg"
                  alt="Google"
                  className="w-5 h-5"
                />
                Continue with Google
              </button>
              <button
                type="button"
                className="flex items-center justify-center gap-2 px-4 py-2 font-medium transition border border-gray-200 rounded-md shadow-sm hover:bg-gray-50"
                onClick={() => handleOAuthSignIn("github")}
              >
                <img
                  src="https://www.svgrepo.com/show/512317/github-142.svg"
                  alt="GitHub"
                  className="w-5 h-5"
                />
                Continue with GitHub
              </button>
            </div>
          )}

          {/* Divider - Only show when online */}
          {isOnline && (
            <div className="relative flex items-center my-5">
              <div className="flex-grow border-t border-gray-200" />
              <span className="mx-3 text-xs text-gray-400">or</span>
              <div className="flex-grow border-t border-gray-200" />
            </div>
          )}

          {/* Offline notice */}
          {!isOnline && (
            <div className="p-3 mb-4 border border-orange-200 rounded-lg bg-orange-50">
              <p className="text-sm text-orange-700">
                You're offline.{" "}
                {tab === "signin" ? "Sign in" : "Account creation"} will work
                locally and sync when you're back online.
              </p>
            </div>
          )}

          {/* Tabs */}
          <div className="grid grid-cols-2 mb-5 overflow-hidden border border-gray-200 rounded-md">
            <button
              className={`py-2 font-semibold transition ${
                tab === "signin"
                  ? "bg-gray-100 text-black"
                  : "bg-white text-gray-400"
              }`}
              onClick={() => setTab("signin")}
              style={{ borderRight: "1px solid #e5e7eb" }}
            >
              Sign In
            </button>
            <button
              className={`py-2 font-semibold transition ${
                tab === "signup"
                  ? "bg-white text-black border border-gray-900 border-b-0 border-t-0 border-l-0 rounded-none"
                  : "bg-white text-gray-400"
              }`}
              onClick={() => setTab("signup")}
              style={
                tab === "signup"
                  ? {
                      borderTop: "none",
                      borderLeft: "none",
                      borderRight: "2px solid #111827",
                      borderBottom: "2px solid #111827",
                    }
                  : {}
              }
            >
              Sign Up
            </button>
          </div>

          {tab === "signin" && (
            <form className="space-y-4" onSubmit={handleSignIn}>
              <div>
                <label className="block mb-1 text-sm font-semibold">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  className="w-full px-3 py-2 text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-300"
                  placeholder="farmer@example.com"
                  autoFocus
                  required
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-semibold">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="current-password"
                    className="w-full px-3 py-2 pr-10 text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-300"
                    required
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute text-gray-400 -translate-y-1/2 right-2 top-1/2"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={signInLoading}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white rounded-md py-2.5 font-semibold mt-2 transition"
              >
                {signInLoading ? "Signing in..." : "Sign In"}
              </button>
              {signInError && (
                <div className="text-xs text-center text-red-500">
                  {signInError}
                </div>
              )}
              <div className="flex flex-col items-center gap-1 mt-2 text-xs text-center text-gray-500">
                <div>
                  <span className="inline-block w-3 h-3 mr-1 align-middle border border-green-400 rounded-full" />
                  Your data is encrypted and secure
                </div>
              </div>
            </form>
          )}

          {tab === "signup" && (
            <form className="space-y-4" onSubmit={handleSignUp}>
              <div>
                <label className="block mb-1 text-sm font-semibold">
                  Full Name
                </label>
                <input
                  type="text"
                  name="fullName"
                  autoComplete="name"
                  className="w-full px-3 py-2 text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-300"
                  placeholder="John Farmer"
                  autoFocus
                  required
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-semibold">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  className="w-full px-3 py-2 text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-300"
                  placeholder="farmer@example.com"
                  required
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-semibold">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="new-password"
                    className="w-full px-3 py-2 pr-10 text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-300"
                    required
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute text-gray-400 -translate-y-1/2 right-2 top-1/2"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={signUpLoading}
                className="w-full bg-green-600 hover:bg-green-700 text-white rounded-md py-2.5 font-semibold mt-2 transition cursor-pointer"
              >
                {signUpLoading ? "Creating Account..." : "Create Account"}
              </button>
              {signUpError && (
                <div className="text-xs text-center text-red-500">
                  {signUpError}
                </div>
              )}
              {signUpSuccess && (
                <div className="text-xs text-center text-green-600">
                  {signUpSuccess}
                </div>
              )}
              <div className="flex flex-col items-center gap-1 mt-2 text-xs text-center text-gray-500">
                <div>
                  <span className="inline-block w-3 h-3 mr-1 align-middle border border-green-400 rounded-full" />
                  Your data is encrypted and secure
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
